import React, {useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Toast} from "primereact/toast";
import {executeAiProposal} from "../../models/ai/ai-action";
import {useAi} from "../../models/ai/use-ai";
import {usePermission} from "../usePermission";
import {useTranslate} from "../useTranslate";
import AiActionWizard from "./ai-action-wizard";

function AiProjectCreationButton({context, initialType = "project", onCompleted}) {
    const translate = useTranslate();
    const {ai} = useAi();
    const permission = usePermission();
    const workspaceStatus = useSelector(
        (state) => state.app.workspaceMembershipData?.workspace?.workspaceStatus || "active",
    );
    const toast = useRef(null);
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);

    const allowedTypes = [
        ...(permission.can("create-new-projects") ? ["project"] : []),
        ...(permission.can("create-new-goals") ? ["goal"] : []),
    ];
    const allowedEntityTypes = [
        ...(permission.can("create-new-projects") ? ["project"] : []),
        ...(permission.can("canManageTasks") ? ["deliverable", "task"] : []),
        ...(permission.can("manage-teams") ? ["team"] : []),
    ];

    const generate = async ({creationType, description}) => {
        const result = await ai.generateCreationDraft({
            creationType,
            description,
            pageContext: context,
        });
        return result?.suggestion;
    };

    const submit = async (proposalInput) => {
        setLoading(true);
        try {
            const result = await executeAiProposal(
                ai,
                proposalInput,
                translate("ai.action.approvalReason"),
            );
            toast.current?.show({
                severity: result.status === "executed" ? "success" : "info",
                summary: translate(`ai.action.${result.status}`),
                detail: translate(`ai.action.${result.status}Description`),
                life: 5000,
            });
            setVisible(false);
            await onCompleted?.();
        } catch (error) {
            toast.current?.show({
                severity: "error",
                summary: translate("ai.action.failed"),
                detail: translate(error?.code || error?.message || "ai.error.generic"),
                life: 5000,
            });
            throw error;
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Toast ref={toast} position="bottom-left"/>
            <Button
                type="button"
                outlined
                className="text-xs gap-2"
                icon="pi pi-sparkles"
                label={translate("ai.creation.button")}
                disabled={!allowedTypes.length || workspaceStatus !== "active"}
                onClick={() => setVisible(true)}
            />
            <AiActionWizard
                visible={visible}
                initialType={allowedTypes.includes(initialType) ? initialType : allowedTypes[0]}
                allowedTypes={allowedTypes}
                allowedEntityTypes={allowedEntityTypes}
                loading={loading}
                onHide={() => !loading && setVisible(false)}
                onGenerate={generate}
                onSubmit={submit}
                draftContext={context}
            />
        </>
    );
}

export default AiProjectCreationButton;
